import React, { useMemo } from "react";
import IconHome from "../../../styles/icons/IconHome";

interface Props {
  inputValue: any;
  record: any;
  leadType?: string;
}

const isFilled = (val: any) => {
  if (!val) return false;
  if (typeof val === "string") return val.trim() !== "";
  if (typeof val === "object") return Object.keys(val).length > 0;
  return true;
};

const LeadProgressChecklist = ({ inputValue, record, leadType }: Props) => {
  const isConsumer = leadType?.toLowerCase() === "customer";

  const sections = useMemo(() => {
    return [
      {
        name: "Customer Details",
        fields: ["customer"],
        description: "Customer selected",
      },
      {
        name: "Job Details",
        fields: ["job"],
        description: "Job selected",
      },
      {
        name: "Lead Details",
        fields: isConsumer
          ? ["firstName", "mobile", "email"]
          : ["firstName", "jobTitle", "company", "mobile", "email"],
        description: "Required lead information",
      },

      // hide Business Details for Consumer
      ...(!isConsumer
        ? [
            {
              name: "Business Details",
              fields: ["businessType"],
              description: "Business category",
            },
          ]
        : []),

      {
        name: "Address Details",
        fields: ["address1"],
        description: "Location details",
      },

      {
        name: "Call Recording",
        isFile: true,
        fields: ["record"],
        description: "Audio required",
      },
    ];
  }, [isConsumer]);

  const processedSections = useMemo(() => {
    return sections.map((section) => {
      const completed = section.isFile
        ? !!record
        : section.fields.every((f) => isFilled(inputValue?.[f]));

      const filledCount = section.isFile
        ? record
          ? 1
          : 0
        : section.fields.filter((f) => isFilled(inputValue?.[f])).length;

      return {
        ...section,
        completed,
        filledCount,
        total: section.fields.length,
      };
    });
  }, [sections, inputValue, record]);

  const progress = useMemo(() => {
    const total = processedSections.length;
    const done = processedSections.filter((s) => s.completed).length;

    return total ? Math.round((done / total) * 100) : 0;
  }, [processedSections]);

  return (
    <div className="lead-progress-card no-mobile">
      <div className="lead-progress-card__header">
        <h4>Lead Setup Progress</h4>
        <span>{progress}% Completed</span>
      </div>

      <div className="lead-progress-card__bar">
        <div
          className="lead-progress-card__bar-fill"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="lead-progress-card__list">
        {processedSections.map((section, index) => (
          <div key={section.name} className="lead-progress-card__item">
            <div className="lead-progress-card__icon">
              {section.completed ? (
                <IconHome breadcrumb="right" activeColor="#16a34a" />
              ) : section.filledCount > 0 ? (
                <span className="lead-progress-card__dot">●</span>
              ) : (
                <div className="lead-progress-card__circle" />
              )}
            </div>

            <div className="lead-progress-card__content">
              <div className="lead-progress-card__title">{section.name}</div>

              <div className="lead-progress-card__desc">
                {section.completed
                  ? "Completed"
                  : section.filledCount > 0
                  ? `${section.total - section.filledCount} fields remaining`
                  : section.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LeadProgressChecklist;
